import React, { useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Modal } from '../../components/UI/Modal';
import { Button } from '../../components/UI/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { AlertCircle, ChefHat } from 'lucide-react';
import { Order, Chef, AssignChefsForm } from '../../types/types';
import { CustomDropdown } from '../../components/UI/CustomDropdown';

interface AssignChefsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: Order | null;
  assignFormData: AssignChefsForm;
  chefs: Chef[];
  error: string;
  submitting: string | null;
  assignChefs: (orderId: string) => void;
  setAssignForm: (formData: AssignChefsForm) => void;
  isRtl: boolean;
}

const translateUnit = (unit: string, isRtl: boolean) => {
  const translations: Record<string, { ar: string; en: string }> = {
    'كيلو': { ar: 'كيلو', en: 'kg' },
    'قطعة': { ar: 'قطعة', en: 'piece' },
    'علبة': { ar: 'علبة', en: 'pack' },
    'صينية': { ar: 'صينية', en: 'tray' },
    'kg': { ar: 'كيلو', en: 'kg' },
    'piece': { ar: 'قطعة', en: 'piece' },
    'pack': { ar: 'علبة', en: 'pack' },
    'tray': { ar: 'صينية', en: 'tray' },
    'كجم': { ar: 'كيلو', en: 'kg' },
  };
  const translation = translations[unit];
  if (!translation) return isRtl ? 'وحدة' : 'unit';
  return isRtl ? translation.ar : translation.en;
};

export const AssignChefsModal: React.FC<AssignChefsModalProps> = ({
  isOpen,
  onClose,
  selectedOrder,
  assignFormData,
  chefs,
  error,
  submitting,
  assignChefs,
  setAssignForm,
  isRtl,
}) => {
  const { t } = useLanguage();

  // إعداد خيارات الشيفات حسب القسم مع دعم اختلاف النقاط في departmentId
  const availableChefsByDepartment = useMemo(() => {
    const map = new Map<string, Chef[]>();
    
    chefs.forEach((chef) => {
      if (Array.isArray(chef.department) && chef.department.length > 0) {
        chef.department.forEach((dept) => {
          // استخراج departmentId مع دعم كلا الشكلين (مع النقاط وبدونها)
          const deptId = dept.id || dept._id;
          if (deptId) {
            // إنشاء مفتوحين: واحد مع النقاط وآخر بدون
            const normalizedDeptId = deptId.replace(/\./g, '');
            const originalDeptId = deptId;
            
            // إضافة الشيف إلى المفتاح المعياري
            if (!map.has(normalizedDeptId)) {
              map.set(normalizedDeptId, []);
            }
            const chefsInDept = map.get(normalizedDeptId)!;
            if (!chefsInDept.some(c => c.userId === chef.userId)) {
              chefsInDept.push(chef);
            }
            
            // إضافة الشيف إلى المفتاح الأصلي أيضًا إذا كان مختلفًا
            if (normalizedDeptId !== originalDeptId) {
              if (!map.has(originalDeptId)) {
                map.set(originalDeptId, []);
              }
              const chefsInOriginalDept = map.get(originalDeptId)!;
              if (!chefsInOriginalDept.some(c => c.userId === chef.userId)) {
                chefsInOriginalDept.push(chef);
              }
            }
          }
        });
      }
    });
    
    return map;
  }, [chefs]);

  const updateAssignment = useCallback(
    (index: number, field: string, value: string | boolean) => {
      setAssignForm({
        items: assignFormData.items.map((i, idx) =>
          idx === index ? { ...i, [field]: value } : i
        ),
      });
    },
    [assignFormData.items, setAssignForm]
  );

  // مزامنة الحالة الأولية للنصوص والتعيينات مع دعم اختلاف النقاط
  useEffect(() => {
    if (!selectedOrder) return;

    const updatedItems = assignFormData.items.map((item) => {
      const orderItem = selectedOrder.items.find((i) => i._id === item.itemId);
      
      // الحصول على departmentId من العنصر
      const departmentId = orderItem?.department?._id || '';
      
      // تطبيع departmentId (إزالة النقاط) للبحث
      const normalizedDeptId = departmentId.replace(/\./g, '');
      
      // البحث عن الشيفات باستخدام كلا المفتاحين
      let availableChefs: Chef[] = [];
      
      // المحاولة أولاً بالمفتاح الأصلي
      availableChefs = availableChefsByDepartment.get(departmentId) || [];
      
      // إذا لم يوجد شيفات، المحاولة بالمفتاح المعياري (بدون نقاط)
      if (availableChefs.length === 0) {
        availableChefs = availableChefsByDepartment.get(normalizedDeptId) || [];
      }
      
      // المحاولة أيضًا بالبحث في جميع الشيفات إذا لم يتم العثور
      if (availableChefs.length === 0 && departmentId) {
        availableChefs = chefs.filter(chef => 
          Array.isArray(chef.department) && 
          chef.department.some(dept => {
            const chefDeptId = dept.id || dept._id;
            return chefDeptId === departmentId || 
                   chefDeptId === normalizedDeptId ||
                   chefDeptId.replace(/\./g, '') === normalizedDeptId;
          })
        );
      }

      let assignedTo = item.assignedTo;
      if (assignedTo === '' && availableChefs.length === 1 && !item.rejected) {
        assignedTo = availableChefs[0].userId;
      }

      return {
        ...item,
        assignedTo,
        unit: translateUnit(orderItem?.unit || 'unit', isRtl),
        rejected: item.rejected || false,
        rejectReason: item.rejectReason || '',
      };
    });

    const hasChanges = updatedItems.some(
      (item, idx) =>
        item.assignedTo !== assignFormData.items[idx].assignedTo ||
        item.unit !== assignFormData.items[idx].unit ||
        item.rejected !== assignFormData.items[idx].rejected ||
        item.rejectReason !== assignFormData.items[idx].rejectReason
    );

    if (hasChanges) {
      setAssignForm({ items: updatedItems });
    }
  }, [assignFormData.items, availableChefsByDepartment, selectedOrder, setAssignForm, isRtl, chefs]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isRtl ? `تعيين الشيفات لطلب ${selectedOrder?.orderNumber}` : `Assign Chefs to Order #${selectedOrder?.orderNumber}`}
      size="md"
      className="bg-white rounded-lg shadow-xl border border-gray-100"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          assignChefs(selectedOrder?.id || '');
        }}
        className="space-y-5"
      >
        {assignFormData.items.map((item, index) => {
          const orderItem = selectedOrder?.items.find((i) => i._id === item.itemId);
          const departmentId = orderItem?.department?._id || '';
          
          // تطبيع departmentId للبحث
          const normalizedDeptId = departmentId.replace(/\./g, '');
          
          // البحث عن الشيفات المتاحة مع دعم اختلاف النقاط
          let availableChefs: Chef[] = [];
          
          // المحاولة أولاً بالمفتاح الأصلي
          availableChefs = availableChefsByDepartment.get(departmentId) || [];
          
          // إذا لم يوجد شيفات، المحاولة بالمفتاح المعياري
          if (availableChefs.length === 0) {
            availableChefs = availableChefsByDepartment.get(normalizedDeptId) || [];
          }
          
          // البحث البديل إذا لم يتم العثور
          if (availableChefs.length === 0 && departmentId) {
            availableChefs = chefs.filter(chef => 
              Array.isArray(chef.department) && 
              chef.department.some(dept => {
                const chefDeptId = dept.id || dept._id;
                return chefDeptId === departmentId || 
                       chefDeptId === normalizedDeptId ||
                       (chefDeptId && chefDeptId.replace(/\./g, '') === normalizedDeptId);
              })
            );
          }

          // إعداد خيارات الشيفات للـ CustomDropdown
          const chefOptions = availableChefs.map((chef) => {
            let deptName = isRtl ? 'غير معروف' : 'Unknown';
            
            if (Array.isArray(chef.department)) {
              // البحث عن القسم المناسب مع دعم اختلاف النقاط
              const matchingDept = chef.department.find(dept => {
                const chefDeptId = dept.id || dept._id;
                return chefDeptId === departmentId || 
                       chefDeptId === normalizedDeptId ||
                       (chefDeptId && chefDeptId.replace(/\./g, '') === normalizedDeptId);
              });
              
              if (matchingDept) {
                deptName = matchingDept.name || (isRtl ? 'غير معروف' : 'Unknown');
              } else if (chef.department.length > 0) {
                deptName = chef.department[0].name || (isRtl ? 'غير معروف' : 'Unknown');
              }
            }
            
            return {
              value: chef.userId,
              label: `${chef.displayName} (${deptName})`
            };
          });

          // إضافة خيار "غير معين" إذا كان هناك شيفات متاحة
          if (chefOptions.length > 0) {
            chefOptions.unshift({
              value: '',
              label: isRtl ? '-- اختر شيف --' : '-- Select Chef --'
            });
          }

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
              className="space-y-2"
            >
              <div className="flex justify-between items-center flex-wrap gap-2">
                <label
                  className={`block text-sm font-medium text-gray-900 ${isRtl ? 'text-right' : 'text-left'}`}
                >
                  {isRtl
                    ? `تعيين شيف لـ ${orderItem?.displayProductName} (${item.quantity} ${item.unit})`
                    : `Assign chef to ${orderItem?.displayProductName} (${item.quantity} ${item.unit})`}
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.rejected}
                    onChange={(e) => updateAssignment(index, 'rejected', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-red-600">{isRtl ? 'رفض العنصر' : 'Reject Item'}</span>
                </label>
              </div>

              {item.rejected ? (
                <select
                  value={item.rejectReason || ''}
                  onChange={(e) => updateAssignment(index, 'rejectReason', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500 bg-red-50"
                >
                  <option value="">{isRtl ? 'اختر سبب الرفض' : 'Select reject reason'}</option>
                  <option value="تالف">{isRtl ? 'تالف' : 'Damaged'}</option>
                  <option value="منتج خاطئ">{isRtl ? 'منتج خاطئ' : 'Wrong product'}</option>
                  <option value="كمية زائدة">{isRtl ? 'كمية زائدة' : 'Excess quantity'}</option>
                  <option value="أخرى">{isRtl ? 'أخرى' : 'Other'}</option>
                </select>
              ) : (
                <div>
                  <CustomDropdown
                    value={item.assignedTo}
                    onChange={(value) => updateAssignment(index, 'assignedTo', value as string)}
                    options={chefOptions}
                    placeholder={isRtl ? 'اختر شيف' : 'Select Chef'}
                    ariaLabel={isRtl ? 'اختر شيف' : 'Select Chef'}
                    className="w-full"
                  />
                  {chefOptions.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1">
                      {isRtl ? 'لا يوجد شيفات متاحة لهذا القسم' : 'No chefs available for this department'}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-600 text-sm">{error}</span>
          </motion.div>
        )}

        <div className={`flex justify-end gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <Button
            variant="secondary"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md px-4 py-2 text-sm shadow-sm"
            aria-label={isRtl ? 'إلغاء' : 'Cancel'}
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting !== null}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm shadow-sm flex items-center gap-2"
            aria-label={isRtl ? 'تعيين الشيفات' : 'Assign Chefs'}
          >
            {submitting ? (isRtl ? 'جارٍ التعيين...' : 'Assigning...') : (
              <>
                <ChefHat className="w-4 h-4" />
                {isRtl ? 'تعيين الشيفات' : 'Assign Chefs'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AssignChefsModal;